import { useMemo } from "react"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js"

import { addCommasToNumber, capitalize } from "../../../../utility"
import { colorPaletteOptions, GENDER_COLOR_MAP } from "../../../../constants"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { lang } from "../../../../constants/languages"
import NotFound from "../../../NotFound"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

export type TrendDemographicStreamMap = Record<string, Record<string, number>>

const DemographicChart = ({ trendDemographicStreamMap }: { trendDemographicStreamMap?: TrendDemographicStreamMap }) => {
  const buildPercentsList = (points: number[], totalStreams: number) => {
    if (!totalStreams) return points.map(() => 0)
    const percents = points.map((v, i) => {
      const p = Number(((v / totalStreams) * 100).toFixed(1))
      return p
    })

    return percents
  }

  const baseChart = useMemo(() => {
    const map = trendDemographicStreamMap || {}

    const datasetKeys = Object.keys(map)
    const ageSet = new Set<string>()
    datasetKeys.forEach((k) => {
      Object.keys(map[k] || {}).forEach((ageBucket) => ageSet.add(ageBucket))
    })

    const parseAgeStart = (s: string) => {
      const trimmed = (s || "").trim()
      const m = trimmed.match(/^(\d+)/)
      if (m?.[1]) return Number(m[1])
      return Number.POSITIVE_INFINITY
    }

    const labels = Array.from(ageSet).sort((a, b) => {
      const aa = parseAgeStart(a)
      const bb = parseAgeStart(b)
      if (aa !== bb) return aa - bb
      return a.localeCompare(b)
    })

    let totalStreams = 0
    let totalPercent = 0

    datasetKeys.forEach((key) => {
      labels.forEach((ageBucket) => {
        totalStreams += Number(map[key]?.[ageBucket] ?? 0)
      })
    })

    const datasets = datasetKeys
      .sort((a, b) => a.localeCompare(b))
      .map((key, index) => {
        const baseColor = GENDER_COLOR_MAP[capitalize(key) as keyof typeof GENDER_COLOR_MAP] || colorPaletteOptions[index % 10]

        const points = labels.map((ageBucket) => Number(map[key]?.[ageBucket] ?? 0))

        const percents = buildPercentsList(points, totalStreams)

        if (index === datasetKeys.length - 1) {
          const total = [...percents].slice(0, -1).reduce((sum, n) => sum + n, 0)
          totalPercent += total
          percents[percents.length - 1] = Number((100 - totalPercent).toFixed(1))
        } else {
          const total = percents.reduce((sum, n) => sum + n, 0)
          totalPercent += total
        }

        return {
          label: capitalize(key),
          data: points,
          percents,
          backgroundColor: baseColor,
          borderColor: baseColor,
          borderWidth: 0,
          borderRadius: 0,
          barThickness: "flex",
          categoryPercentage: 0.8,
          barPercentage: 0.6
        }
      })

    const ageTotals = labels.map((ageBucket) => {
      return datasetKeys.reduce((sum, key) => sum + Number(map[key]?.[ageBucket] ?? 0), 0)
    })

    return { labels, datasets, totalStreams, ageTotals }
  }, [trendDemographicStreamMap])
  const chartData = useMemo(() => {
    return {
      labels: baseChart.labels,
      datasets: baseChart.datasets.map((ds: any, i: number) => {
        //const isOtherHovered = !(hoveredIndex.datasetIndex === i || hoveredIndex.datasetIndex === -1)
        const baseColor = ds.backgroundColor

        return {
          ...ds,
          // Keep hovered dataset solid, fade the rest
          backgroundColor: baseColor,
          borderColor: "red"
        }
      })
    }
  }, [baseChart])

  const getStackPercentAtIndex = (idx: number) => {
  const sum = (baseChart.datasets || []).reduce((acc: number, ds: any) => {
    const v = Number(ds?.percents?.[idx] ?? 0)
    return acc + v
  }, 0)

  // 1 ondalıkta toparla
  return Number(sum.toFixed(1))
}

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false as const,

    interaction: {
      intersect: false,
      mode: "nearest" as const,
      axis: "x" as const
    },

    plugins: {
      datalabels: {
        display: (ctx: any) => {
          return ctx.datasetIndex === ctx.chart.data.datasets.length - 1
        },
        anchor: "end" as const,
        align: "end" as const,
        clamp: true,
        offset: 2,
        formatter: (_value: any, ctx: any) => {
          const idx = ctx.dataIndex
          const pct = getStackPercentAtIndex(idx)
          return `${pct}%`
        },
        color: "#111",
        font: {
          family: "Inter",
          weight: 600,
          size: 13
        }
      },

      tooltip: {
        enabled: true,
        mode: "index" as const,
        intersect: false,
        displayColors: true,
        usePointStyle: true,
        backgroundColor: "#333",
        xAlign: "center" as const,
        yAlign: "bottom" as const,
        caretSize: 0,
        caretPadding: 10,
        padding: 10,
        titleFont: {
          family: "Inter"
        },
        bodyFont: {
          family: "Inter",
          weight: 400,
          size: 12
        },
        callbacks: {
          // age bucket (y-axis label)

          title: (items: any[]) => items?.[0]?.label ?? "",
          beforeBody: (items: any[]) => {
            return [`${lang.analytics.total_stream}: ${addCommasToNumber(baseChart.totalStreams)}`]
          },
          label: (ctx: any) => {
            const datasetLabel = ctx.dataset?.label ?? ""
            const value = Number(ctx.parsed?.y ?? 0) // horizontal bar -> x
            const pct = Number(ctx.dataset?.percents?.[ctx.dataIndex] ?? 0)
            return [
              `${datasetLabel}: ${value.toLocaleString("en-US")}`,
              //`${lang.analytics.total_stream}: ${addCommasToNumber(baseChart.totalStreams)}`,
              `${lang.analytics.percentage_of_total.replace("<param>", `${pct}%`)}`
            ]
          },

          labelColor: (ctx: any) => {
            const c = ctx.dataset?.backgroundColor
            const color = Array.isArray(c) ? c[ctx.dataIndex] : c
            return {
              backgroundColor: color || "#999",
              borderColor: "transparent",
              borderWidth: 0
            }
          },
          labelPointStyle: () => ({
            pointStyle: "circle" as const,
            rotation: 0
          })
        }
      },
      hover: { intersect: false },
      legend: {
        display: true,
        position: "bottom" as const,
        align: "end" as const,
        labels: {
          font: { family: "Inter", weight: 500, size: 13, lineHeight: "13px" },
          usePointStyle: true,
          pointStyle: "circle" as const,
          boxWidth: 8,
          boxHeight: 8
        }
      }
    },

    scales: {
      y: {
        stacked: true,
        grace: "10%",
        grid: { display: false, drawTicks: true },
        border: { display: true, dash: [3, 3] },
        ticks: {
          color: "#999",
          font: { family: "Inter", weight: 500, size: 11, lineHeight: "12px" }
        }
      },
      x: {
        stacked: true, // set true if you want stacked bars
        grid: { display: true, drawBorder: true, drawTicks: true, drawOnChartArea: true },
        border: { display: true, dash: [3, 3] },
        ticks: {
          color: "#999",
          font: { family: "Inter", weight: 500, size: 11, lineHeight: "12px" }
        }
      }
    }
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        padding: 10,
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: " var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div style={{ width: "100%", height: "5%", paddingLeft: 12, paddingTop: 4 }}>
        <KTitleSpan
          text={lang.trends.headers.age_breakdown}
          fontSize={16}
          color="#737373"
          lineHeight="20px"
          fontWeight={500}
        />
      </div>

      <div className="flex items-center justify-center" style={{ width: "100%", height: "95%", padding: "20px" }}>
        {chartData.labels.length > 0 ? (
          <Bar data={chartData} options={options} style={{ width: "100%", height: "100%" }} />
        ) : (
          <div className="w-[70%]  max-w-[320px]">
            <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
          </div>
        )}
      </div>
    </div>
  )
}

export default DemographicChart
